import { WallPaintingSettings, useWallPaintingProcess } from 'dataQueries/photoStudio.query'

import { JobStatus } from 'constants/API'
import { JobType, useAIVisualsResult, useJobPoll } from 'dataQueries'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { logger } from 'utils/logging'
import { PhotoStudioGenerationStepId } from '../generationSteps'

interface UseWallPaintingProcessorProps {
  assignmentId: string
  sourceVisualFilename: string
  getLatestPreviewFilename: () => string | null
  onProgressUpdate?: (step: string) => void
  onComplete?: (resultUrl: string) => void
  onError?: (error: any) => void
}

/**
 * Reusable Wall Painting processor hook
 * Handles the complete flow from processing to result display
 */
export const useWallPaintingProcessor = ({
  assignmentId,
  sourceVisualFilename,
  getLatestPreviewFilename,
  onProgressUpdate,
  onComplete,
  onError
}: UseWallPaintingProcessorProps) => {
  const wallPaintingProcess = useWallPaintingProcess()
  const wallPaintingJobPoll = useJobPoll(JobType.WALL_PAINTING)
  const wallPaintingResult = useAIVisualsResult() // Reuse the same result endpoint
  const { t } = useTranslation(['photo_studio_editing'])

  const processWallPainting = useCallback(async (
    settings: WallPaintingSettings,
    useTempBucket: boolean,
    previousJobId?: string
  ) => {
    let lastJobStatus: string | undefined
    let lastJobError: string | undefined
    try {
      onProgressUpdate?.(PhotoStudioGenerationStepId.START_PROCESSING)

      // Step 1: Start Wall Painting processing
      const currentPreview = getLatestPreviewFilename()
      const inputVisualFilename = currentPreview || sourceVisualFilename
      const processResponse = await wallPaintingProcess.mutateAsync({
        assignmentId,
        ...((!previousJobId || !!useTempBucket) && { visualFilename: inputVisualFilename }),
        settings,
        ...(previousJobId && { reExecuteFromPreviousJob: previousJobId }),
        useTempBucket,
      })

      onProgressUpdate?.(PhotoStudioGenerationStepId.UPLOAD_MASK)

      // Step 2: Poll job until completion
      const jobResult = await wallPaintingJobPoll.mutateAsync({
        jobId: processResponse.id
      })
      lastJobStatus = jobResult.status
      lastJobError = jobResult.error

      if (jobResult.status === JobStatus.FINISHED) {
        onProgressUpdate?.(PhotoStudioGenerationStepId.PROCESSING_COMPLETE)

        // Step 3: Get the AI result
        const aiResult = await wallPaintingResult.refetchWithParams(assignmentId, processResponse.id)

        if (aiResult.signedURL) {
          onComplete?.(aiResult.signedURL)
          return {
            success: true,
            resultUrl: aiResult.signedURL,
            jobId: processResponse.id
          }
        } else {
          throw new Error('No AI result available')
        }
      } else if (jobResult.status === JobStatus.FAILED) {
        // Throw error for failed Wall Painting job - toast will be shown by controller's onError callback
        throw new Error(t('virtual_staging_error'))
      } else {
        throw new Error(`Wall Painting job failed with status: ${jobResult.status}`)
      }
    } catch (error) {
      logger.error('Wall Painting: Processing failed', error, {
        context: {
          component: 'PhotoStudio',
          action: 'wallPaintingProcess',
          metadata: {
            assignmentId,
            sourceVisualFilename,
            jobStatus: lastJobStatus,
            jobError: lastJobError,
          }
        }
      })
      onError?.(error)
      return {
        success: false,
        error
      }
    }
  }, [
    assignmentId,
    sourceVisualFilename,
    getLatestPreviewFilename,
    wallPaintingProcess,
    wallPaintingJobPoll,
    wallPaintingResult,
    onProgressUpdate,
    onComplete,
    onError,
    t
  ])

  return {
    processWallPainting,
    isProcessing: wallPaintingProcess.isPending || wallPaintingJobPoll.isPending,
    error: wallPaintingProcess.error || wallPaintingJobPoll.error
  }
}
